import * as THREE from 'three'
import { ImportedModelObjectMetadataService } from '../../metadata'
import { ImportedModelMeshIdentityResolver } from '../../selection'
import { isImportedModelTopologyHost } from '../../topology/imported-model-topology-runtime'
import type { ComposedAssemblyResult } from '../../loaders'

/**
 * 导入模型 occurrence 与原始显示 Mesh 的运行时索引。
 *
 * 该索引只消费加载链已经确认的 `assemblyNodeId`、definition 映射和正式 metadata，
 * 不使用 Object3D.uuid、对象名称或场景遍历顺序推断业务身份。
 */
export class ImportedModelOccurrenceIndex {
  /** occurrence 身份到不可变运行时描述的索引。 */
  private readonly occurrenceById = new Map<string, PuppyCAD.ImportedModelInstancingOccurrence>()

  /** definition 身份到有序 occurrence 列表的索引。 */
  private readonly occurrencesByDefinition = new Map<
    string,
    readonly PuppyCAD.ImportedModelInstancingOccurrence[]
  >()

  /** 索引是否已经结束生命周期。 */
  private disposed = false

  /**
   * 从已经完成项目语义收口的组合结果建立 occurrence 索引。
   *
   * @param result 已完成 sceneBindings 和 definition 身份重绑定的装配结果。
   * @param runtimeOccurrences 由线性阵列等 cad-core 领域贡献的正式运行时 occurrence。
   */
  public constructor(
    result: ComposedAssemblyResult,
    runtimeOccurrences: readonly PuppyCAD.ImportedModelInstancingOccurrence[] = [],
  ) {
    const definitionBuckets = new Map<string, PuppyCAD.ImportedModelInstancingOccurrence[]>()
    const objectsByOccurrenceId = new Map<string, THREE.Object3D[]>()

    // 最终项目组合可能重建 sceneBindings；Object3D.metadata 是挂载后 occurrence 身份的权威镜像。
    result.root.traverse((object) => {
      const occurrenceId = ImportedModelObjectMetadataService.peekOwnMetadata(object)?.assemblyNodeId?.trim()
      if (!occurrenceId) {
        return
      }
      const objects = objectsByOccurrenceId.get(occurrenceId) ?? []
      objects.push(object)
      objectsByOccurrenceId.set(occurrenceId, objects)
    })
    Object.entries(result.sceneBindings.byAssemblyNodeId).forEach(([occurrenceId, objectIds]) => {
      const objects = objectsByOccurrenceId.get(occurrenceId) ?? []
      for (const objectId of objectIds) {
        const object = result.sceneBindings.byObject3DUuid[objectId]
        if (object && !objects.includes(object)) {
          objects.push(object)
        }
      }
      if (objects.length > 0) {
        objectsByOccurrenceId.set(occurrenceId, objects)
      }
    })
    const occurrenceIds = [...objectsByOccurrenceId.keys()].sort()

    for (const occurrenceId of occurrenceIds) {
      const boundObjects = objectsByOccurrenceId.get(occurrenceId) ?? []
      const rootObject = boundObjects.find((object) => (
        ImportedModelObjectMetadataService.peekOwnMetadata(object)?.componentTransformRoot === true
        && !isImportedModelTopologyHost(object)
      )) ?? boundObjects.find((object) => (
        ImportedModelObjectMetadataService.peekOwnMetadata(object)?.componentTransformRoot === true
      ))
      if (!rootObject) {
        continue
      }

      const sourceMeshes = boundObjects.filter((object): object is THREE.Mesh => (
        object instanceof THREE.Mesh
        && !(object instanceof THREE.InstancedMesh)
        && !isImportedModelTopologyHost(object)
        && ImportedModelMeshIdentityResolver.isImportedVisibleMesh(object)
      ))
      if (sourceMeshes.length === 0) {
        continue
      }

      const definitionKey = result.definitionNodeIdByAssemblyNodeId?.get(occurrenceId)
        ?? result.importedTopologySourceByAssemblyNodeId?.get(occurrenceId)?.definitionNodeId
        ?? this.createRuntimeDefinitionKey(rootObject, sourceMeshes)
      const occurrence = Object.freeze({
        occurrenceId,
        definitionKey,
        rootObject,
        sourceMeshes: Object.freeze([...new Set(sourceMeshes)]),
      })
      this.appendOccurrence(definitionBuckets, occurrence)
    }

    // 运行时领域 occurrence 与加载结果使用同一身份、分桶和重复校验规则，禁止建立第二套实例系统。
    runtimeOccurrences.forEach((runtimeOccurrence) => {
      const occurrenceId = runtimeOccurrence.occurrenceId.trim()
      const definitionKey = runtimeOccurrence.definitionKey.trim()
      if (!occurrenceId || !definitionKey || runtimeOccurrence.sourceMeshes.length === 0) {
        throw new Error('导入模型实例化运行时 occurrence 缺少稳定身份、定义身份或 source Mesh')
      }
      this.appendOccurrence(definitionBuckets, Object.freeze({
        occurrenceId,
        definitionKey,
        rootObject: runtimeOccurrence.rootObject,
        sourceMeshes: Object.freeze([...new Set(runtimeOccurrence.sourceMeshes)]),
      }))
    })

    definitionBuckets.forEach((occurrences, definitionKey) => {
      this.occurrencesByDefinition.set(
        definitionKey,
        Object.freeze([...occurrences].sort((left, right) => (
          left.occurrenceId.localeCompare(right.occurrenceId)
        ))),
      )
    })
  }

  /**
   * 读取全部共享定义身份。
   *
   * @returns 按字典序排列的 definition 身份。
   */
  public listDefinitionKeys(): readonly string[] {
    this.assertAvailable()
    return Object.freeze([...this.occurrencesByDefinition.keys()].sort())
  }

  /**
   * 读取指定共享定义的 occurrence。
   *
   * @param definitionKey 共享定义身份。
   * @returns 当前定义下按稳定 occurrence 身份排序的只读列表。
   */
  public listOccurrencesByDefinition(
    definitionKey: string,
  ): readonly PuppyCAD.ImportedModelInstancingOccurrence[] {
    this.assertAvailable()
    return this.occurrencesByDefinition.get(definitionKey) ?? Object.freeze([])
  }

  /**
   * 读取当前索引中的 occurrence 数量。
   *
   * @returns 可参与实例化分析的 occurrence 数量。
   */
  public getOccurrenceCount(): number {
    this.assertAvailable()
    return this.occurrenceById.size
  }

  /**
   * 向索引和 definition 分桶追加一个 occurrence。
   *
   * @param definitionBuckets 当前可变 definition 分桶。
   * @param occurrence 待追加的不可变 occurrence。
   */
  private appendOccurrence(
    definitionBuckets: Map<string, PuppyCAD.ImportedModelInstancingOccurrence[]>,
    occurrence: PuppyCAD.ImportedModelInstancingOccurrence,
  ): void {
    if (this.occurrenceById.has(occurrence.occurrenceId)) {
      throw new Error(`导入模型实例化 occurrence 身份重复：${occurrence.occurrenceId}`)
    }
    this.occurrenceById.set(occurrence.occurrenceId, occurrence)
    const bucket = definitionBuckets.get(occurrence.definitionKey) ?? []
    bucket.push(occurrence)
    definitionBuckets.set(occurrence.definitionKey, bucket)
  }

  /** 释放全部运行时强引用。 */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this.occurrenceById.clear()
    this.occurrencesByDefinition.clear()
  }

  /**
   * 在旧结果缺少显式 definition 映射时创建只限当前 revision 的共享定义签名。
   *
   * 签名只使用实际共享的 Geometry/Material 对象和 definition 内局部位姿，不使用名称、
   * Object3D.uuid 作为业务身份，也不会进入持久化；不同 occurrence 若共享完全相同的渲染
   * 定义即可安全进入后续 slot 资格判定。
   *
   * @param rootObject 当前 occurrence 变换根。
   * @param sourceMeshes 当前 occurrence 的原始显示 Mesh。
   * @returns 当前运行时 definition 签名。
   */
  private createRuntimeDefinitionKey(
    rootObject: THREE.Object3D,
    sourceMeshes: readonly THREE.Mesh[],
  ): string {
    const inverseRootMatrix = new THREE.Matrix4()
    const localMatrix = new THREE.Matrix4()
    rootObject.updateWorldMatrix(true, false)
    inverseRootMatrix.copy(rootObject.matrixWorld).invert()
    const slots = sourceMeshes.map((sourceMesh) => {
      sourceMesh.updateWorldMatrix(true, false)
      localMatrix.multiplyMatrices(inverseRootMatrix, sourceMesh.matrixWorld)
      const materialKey = (Array.isArray(sourceMesh.material)
        ? sourceMesh.material
        : [sourceMesh.material])
        .map(material => material.uuid)
        .join(',')
      const matrixKey = localMatrix.elements
        .map(value => Math.round(value * 1e8) / 1e8)
        .join(',')
      return `${sourceMesh.geometry.uuid}:${materialKey}:${matrixKey}`
    }).sort()
    return `runtime-definition:${slots.join(';')}`
  }

  /** 索引释放后禁止继续查询旧 revision 对象。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error('导入模型 occurrence 索引已释放')
    }
  }
}
